import {
  useStytch,
  useStytchSession,
  useStytchUser,
} from "@louise/api/useStytch";
import type {
  WebAuthnAuthenticateResponse,
  WebAuthnAuthenticateStartOptions,
  WebAuthnRegisterResponse,
  WebAuthnRegisterStartOptions,
} from "@stytch/react-native";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Platform } from "react-native";

import { runWithRequiredUserVerification } from "../required-user-verification";

// Stytch's React Native client exposes the TOTP namespace as `stytch.totp`,
// while the web client (`@stytch/react` → `@stytch/vanilla-js`) exposes it
// as `stytch.totps`. Same method surface, different property name. This
// shim reads whichever the current platform provides so callers don't have
// to know.
type TotpNamespace = {
  create: (options: { expiration_minutes: number }) => Promise<{
    totp_id: string;
    secret: string;
    qr_code: string;
    recovery_codes: string[];
  }>;
  authenticate: (options: {
    totp_code: string;
    session_duration_minutes?: number;
  }) => Promise<{ totp_id: string }>;
  recover: (options: {
    recovery_code: string;
    session_duration_minutes?: number;
  }) => Promise<unknown>;
};

function getTotpClient(stytch: {
  totp?: unknown;
  totps?: unknown;
}): TotpNamespace {
  const candidate = stytch.totp ?? stytch.totps;
  if (!candidate) {
    throw new Error("Stytch TOTP client not available on this platform");
  }
  return candidate as TotpNamespace;
}

const API_BASE_URL = process.env.EXPO_PUBLIC_API_BASE_URL!;

type TotpStatus = {
  /** True if the user has *any* 2FA factor enrolled (TOTP, SMS, or the
   *  backend's `trusted_metadata.is_mfa_enrolled` flag). Use this for the
   *  MFA gate decisions. */
  enabled: boolean;
  /** True specifically if the user has a verified TOTP factor. Use this
   *  for UI that only handles TOTP (Disable2FAModal, Setup2FAModal). A
   *  user can be `enabled=true` via SMS but `totpEnrolled=false`. */
  totpEnrolled: boolean;
  /** True if the user has at least one WebAuthn passkey registered (on ANY
   *  domain). Like `totpEnrolled`, this is a factor-specific signal — a user
   *  can be `enabled=true` via a passkey but `totpEnrolled=false`. Use for the
   *  account "enabled" badge. */
  passkeyEnrolled: boolean;
  /** True if the user has a passkey registered for the CURRENT host. A passkey
   *  is bound to its Relying Party (the host it was created on), so a passkey
   *  from another domain can't be authenticated here. Use this — not
   *  `passkeyEnrolled` — to decide whether to OFFER a passkey on the challenge
   *  screen, so we never surface an unusable option. */
  passkeyUsableHere: boolean;
};
type TotpSetup = {
  totpId: string;
  secret: string;
  qrCode: string;
  recoveryCodes: string[];
};

// Mirror of the backend's category map in dashboard-api auth.ts. Real MFA
// = ≥2 distinct factor categories on the session. Keep this in sync with
// the backend so the frontend doesn't kick users to the challenge screen
// for a session the backend would have accepted (or vice versa).
type FactorCategory = "knowledge" | "email" | "possession" | "hardware";

const DELIVERY_METHOD_TO_CATEGORY: Record<string, FactorCategory> = {
  knowledge: "knowledge",
  email: "email",
  sso_saml: "email",
  sso_oidc: "email",
  sms: "possession",
  whatsapp: "possession",
  authenticator_app: "possession",
  // `recovery_code` is deliberately NOT a category, matching dashboard-api
  // (#382): a recovery code is a static string usually stored next to the
  // password, so password + recovery_code from one compromised store must not
  // clear the gate. The API returns 403 mfa_required for such a session, so
  // counting it here only makes the app think it is signed in while every
  // request fails. After a successful recovery the challenge screen instead
  // walks the user through enrolling a new authenticator (see
  // TwoFAChallengeScreen), which adds a real second category.
  crypto_wallet: "possession",
  webauthn_registration: "hardware",
  biometric: "hardware",
};

function categorize(deliveryMethod: string): FactorCategory | undefined {
  if (
    deliveryMethod.startsWith("oauth_") &&
    !deliveryMethod.startsWith("oauth_exchange_")
  ) {
    return "email";
  }
  return DELIVERY_METHOD_TO_CATEGORY[deliveryMethod];
}

type SessionFactor = {
  delivery_method?: string;
  authenticator_app_factor?: unknown;
  webauthn_factor?: unknown;
};

// Some Stytch session factors (notably WebAuthn) carry no delivery_method;
// fall back to the factor-specific payloads the same way the backend does.
export function factorDeliveryMethod(
  factor: SessionFactor | undefined,
): string | undefined {
  if (!factor) return undefined;
  return (
    factor.delivery_method ??
    (factor.authenticator_app_factor ? "authenticator_app" : undefined) ??
    (factor.webauthn_factor ? "webauthn_registration" : undefined)
  );
}

export function sessionSatisfiesMfa(
  factors: readonly SessionFactor[] | null | undefined,
): boolean {
  if (!factors) return false;
  const categories = new Set<FactorCategory>();
  for (const f of factors) {
    const dm = factorDeliveryMethod(f);
    if (!dm) continue;
    const c = categorize(dm);
    if (c) categories.add(c);
  }
  // A hardware factor (WebAuthn passkey, device biometric) is phishing
  // resistant and already bundles possession with a local user check
  // (Face ID / device PIN), so it satisfies MFA on its own. Without this
  // rule a passkey-first sign-in produces a single-category session and
  // MFA-enrolled users get stranded on the challenge screen, because
  // re-authenticating the passkey there never adds a second category.
  return categories.size >= 2 || categories.has("hardware");
}

function useHasSession() {
  const stytch = useStytch();
  try {
    return !!stytch?.session?.getTokens()?.session_jwt;
  } catch {
    return false;
  }
}

function useAuthFetch() {
  const stytch = useStytch();
  return async (
    path: string,
    init?: { body?: Record<string, unknown> },
  ): Promise<Response> => {
    const token = stytch?.session?.getTokens()?.session_jwt;
    const headers: Record<string, string> = token
      ? { Authorization: `Bearer ${token}` }
      : {};
    if (init?.body) headers["Content-Type"] = "application/json";

    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers,
      body: init?.body ? JSON.stringify(init.body) : undefined,
    });
    if (!res.ok) {
      let message = `Request failed with status ${res.status}`;
      try {
        const data = await res.json();
        if (data?.message) message = data.message;
        else if (data?.error) message = data.error;
      } catch {
        // body not JSON; keep default message
      }
      throw new Error(message);
    }
    return res;
  };
}

// Synchronous derivation from already-reactive Stytch state. Returns the
// same { data, isLoading } shape callers were used to so call sites don't
// need to change, but no actual server fetch happens — wrapping a pure
// computation in useQuery just added retry/stale semantics that couldn't
// help anything (there's no endpoint to retry against).
export function use2FAStatus(): {
  data: TotpStatus | undefined;
  isLoading: boolean;
} {
  const { user } = useStytchUser();
  const hasSession = useHasSession();

  if (!hasSession || !user) {
    return { data: undefined, isLoading: false };
  }

  // Source of truth: trusted_metadata.is_mfa_enrolled, which is what the
  // backend gate reads. Fall back to the local TOTP-verified check during
  // the brief window after enroll/disable when Stytch hasn't propagated
  // the trusted_metadata change yet — the two will converge.
  const trustedMetadata = user.trusted_metadata as
    { is_mfa_enrolled?: boolean } | undefined;
  const flag = trustedMetadata?.is_mfa_enrolled === true;
  const hasVerifiedTotp = !!user.totps?.some(
    (t: { verified: boolean }) => t.verified,
  );
  // The Stytch user tracks passkeys in `webauthn_registrations` the same way
  // it tracks TOTPs. The RN SDK's User type doesn't model the field (the web
  // client's does), so read it via a narrow accessor — same approach as the
  // `trusted_metadata` cast above. These are surfaced for the account badge and
  // for deciding whether the challenge screen OFFERS a passkey; they do NOT
  // feed `enabled`, which stays the metadata-driven gate (is_mfa_enrolled) so a
  // passkey enroll must flip that backend flag to force a login challenge.
  const webauthnRegistrations = (
    user as {
      webauthn_registrations?: {
        webauthn_registration_id: string;
        domain?: string;
      }[];
    }
  ).webauthn_registrations;
  const passkeyEnrolled = (webauthnRegistrations?.length ?? 0) > 0;
  // A passkey is bound to the host it was created on (its Relying Party), so a
  // registration from another domain can't be authenticated here. Match on the
  // current host to decide whether a passkey is actually usable on this screen.
  const currentDomain = getWebAuthnDomain();
  const passkeyUsableHere =
    !!currentDomain &&
    (webauthnRegistrations?.some((r) => r.domain === currentDomain) ?? false);

  return {
    data: {
      enabled: flag || hasVerifiedTotp,
      totpEnrolled: hasVerifiedTotp,
      passkeyEnrolled,
      passkeyUsableHere,
    },
    isLoading: false,
  };
}

// Returns whether the current session needs to walk through TwoFAChallenge.
// True iff the user is enrolled AND the session lacks ≥2 distinct factor
// categories — same rule the backend gate uses, so the two can't disagree.
export function useNeeds2FA() {
  const { user } = useStytchUser();
  const { session } = useStytchSession();
  const { data: status, isLoading } = use2FAStatus();

  const sessionOk = sessionSatisfiesMfa(session?.authentication_factors);
  const needs = !!user && !!status?.enabled && !sessionOk;
  return { needs, isLoading };
}

export function useSetup2FA() {
  const stytch = useStytch();

  return useMutation<TotpSetup>({
    mutationFn: async () => {
      const response = await getTotpClient(stytch).create({
        expiration_minutes: 60,
      });
      return {
        totpId: response.totp_id,
        secret: response.secret,
        qrCode: response.qr_code,
        recoveryCodes: response.recovery_codes,
      };
    },
  });
}

export function useVerify2FA() {
  const stytch = useStytch();
  const queryClient = useQueryClient();
  const authFetch = useAuthFetch();

  return useMutation<void, Error, { totpCode: string }>({
    mutationFn: async ({ totpCode }) => {
      const { totp_id } = await getTotpClient(stytch).authenticate({
        totp_code: totpCode,
        session_duration_minutes: 60 * 24,
      });
      await authFetch("/me/2fa/enroll", {
        body: { factor_type: "totp", factor_id: totp_id },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["2fa-status"] });
    },
  });
}

export function useRecover2FA() {
  const stytch = useStytch();

  return useMutation<void, Error, { recoveryCode: string }>({
    mutationFn: async ({ recoveryCode }) => {
      await getTotpClient(stytch).recover({
        recovery_code: recoveryCode.trim().toLowerCase(),
        session_duration_minutes: 60 * 24,
      });
    },
  });
}

// Passkeys are domain-bound: the credential Stytch mints is scoped to a
// Relying Party ID (the host). On web we derive it from the live location so
// the same code works across dev/preview/prod hosts without hardcoding.
// On native there is no `window`; return undefined and let the SDK apply its
// own default (`window.location.hostname` in the web view it drives).
function getWebAuthnDomain(): string | undefined {
  return Platform.OS === "web" && typeof window !== "undefined"
    ? window.location.hostname
    : undefined;
}

// Stytch's ceremony options default to userVerification: "preferred", which
// allows PIN-less authenticators to produce possession-only assertions on web.
// The backend MFA gate (dashboard-api #800) assumes passkeys always include a
// local user check, so we must enforce "required". The Stytch SDK doesn't
// expose this parameter, so we intercept navigator.credentials.create/get for
// the duration of the ceremony. On native (iOS/Android) the platform passkey
// APIs enforce biometric/PIN regardless, so the wrapper is a no-op.
export function withRequiredUserVerification<T>(
  fn: () => Promise<T>,
): Promise<T> {
  if (
    Platform.OS !== "web" ||
    typeof navigator === "undefined" ||
    !navigator.credentials
  ) {
    return fn();
  }

  return runWithRequiredUserVerification(navigator.credentials, fn);
}

// Whether this surface can offer passkey sign-in as a FIRST factor. Web only:
// the legacy RN SDK drives WebAuthn through a web view and is not wired for
// passkey-first login on native; native quick re-entry is tracked in #1901.
export function isPasskeySignInSupported(): boolean {
  return (
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    "PublicKeyCredential" in window
  );
}

// Enrolls a WebAuthn passkey as a SECOND factor. Mirrors useVerify2FA: after
// the client-side registration succeeds we flip the backend enrollment flag via
// POST /me/2fa/enroll so trusted_metadata.is_mfa_enrolled is set — that flag is
// the source of truth for the MFA gate (useNeeds2FA), so without it a
// passkey-enrolled user is never challenged on login. The backend accepts a
// registered passkey as a valid second factor for this flip (dashboard-api).
export function useEnrollPasskey() {
  const stytch = useStytch();
  const queryClient = useQueryClient();
  const authFetch = useAuthFetch();

  return useMutation<WebAuthnRegisterResponse, Error>({
    mutationFn: async () => {
      const options: WebAuthnRegisterStartOptions = {
        domain: getWebAuthnDomain(),
        is_passkey: true,
        session_duration_minutes: 60 * 24,
      };
      const response = await withRequiredUserVerification(() =>
        stytch.webauthn.register(options),
      );
      await authFetch("/me/2fa/enroll", {
        body: {
          factor_type: "webauthn",
          factor_id: response.webauthn_registration_id,
        },
      });
      // The registration response predates the backend metadata update above,
      // so the SDK's cached user still reports MFA as disabled. Refresh the
      // reactive user before the setup modal closes so the account row and
      // login gate immediately observe the authoritative enrollment flag.
      try {
        await stytch.user.get();
      } catch (err) {
        // Enrollment has already committed in both systems. A refresh failure
        // must not report the completed security operation as failed.
        console.error(
          "Failed to refresh Stytch user after passkey enrollment",
          err,
        );
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["2fa-status"] });
    },
  });
}

// Authenticates a passkey. Serves two callers: the 2FA challenge screen
// (step-up on an existing session) and the web auth gate, where it is a
// FIRST factor: called signed-out it runs the browser's discoverable
// credential flow and mints a fresh session. Like the TOTP verify path, no
// explicit navigation is needed: a successful authenticate puts the webauthn
// factor on the session, useStytchSession re-emits, and the relevant gate
// (TwoFAChallengeScreen or the canonical login surface) unmounts automatically.
export function useAuthenticatePasskey() {
  const stytch = useStytch();
  const queryClient = useQueryClient();

  return useMutation<WebAuthnAuthenticateResponse | null, Error>({
    mutationFn: async () => {
      const options: WebAuthnAuthenticateStartOptions = {
        domain: getWebAuthnDomain(),
        is_passkey: true,
        session_duration_minutes: 60 * 24,
      };
      return withRequiredUserVerification(() =>
        stytch.webauthn.authenticate(options),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["2fa-status"] });
    },
  });
}

export function useDisable2FA() {
  const { user } = useStytchUser();
  const stytch = useStytch();
  const queryClient = useQueryClient();
  const authFetch = useAuthFetch();

  return useMutation<void, Error, { totpCode: string }>({
    mutationFn: async ({ totpCode }) => {
      // Backend verifies the TOTP code server-side and flips the
      // enrollment flag. The login challenge already satisfies the MFA
      // session gate (App.tsx hard-gates via useNeeds2FA), so no
      // client-side step-up is needed.
      await authFetch("/me/2fa/unenroll", {
        body: { factor_type: "totp", totp_code: totpCode },
      });

      // Clean up Stytch-side TOTP records. The backend has already flipped
      // is_mfa_enrolled=false (the authoritative source for the gate), so
      // a Stytch deletion failure mid-loop must NOT abort: that would leave
      // half the TOTPs around, which use2FAStatus would then read as still
      // enrolled even though the gate is off. Log and keep going.
      const verifiedTotps =
        user?.totps?.filter((t: { verified: boolean }) => t.verified) ?? [];
      for (const totp of verifiedTotps) {
        try {
          await stytch.user.deleteTOTP((totp as { totp_id: string }).totp_id);
        } catch (err) {
          console.error("Failed to delete Stytch TOTP record", err);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["2fa-status"] });
    },
  });
}

// Disables 2FA for a passkey-enrolled user. The passkey analog of useDisable2FA:
// instead of a fresh TOTP code, we re-authenticate the passkey as fresh proof
// of possession (which also puts a webauthn factor on the session — what the
// backend /me/2fa/unenroll requires), flip is_mfa_enrolled=false, then remove
// the registrations client-side (mirrors deleting TOTP records).
export function useDisablePasskey() {
  const { user } = useStytchUser();
  const stytch = useStytch();
  const queryClient = useQueryClient();
  const authFetch = useAuthFetch();

  return useMutation<void, Error>({
    mutationFn: async () => {
      await withRequiredUserVerification(() =>
        stytch.webauthn.authenticate({
          domain: getWebAuthnDomain(),
          is_passkey: true,
          session_duration_minutes: 60 * 24,
        }),
      );
      await authFetch("/me/2fa/unenroll", {
        body: { factor_type: "webauthn" },
      });

      // The backend already flipped is_mfa_enrolled=false (authoritative for
      // the gate), so a deletion failure must NOT abort — log and keep going,
      // same as the TOTP path.
      const registrations =
        (
          user as {
            webauthn_registrations?: { webauthn_registration_id: string }[];
          }
        ).webauthn_registrations ?? [];
      for (const reg of registrations) {
        try {
          await stytch.user.deleteWebauthnRegistration(
            reg.webauthn_registration_id,
          );
        } catch (err) {
          console.error("Failed to delete Stytch WebAuthn registration", err);
        }
      }
      // Keep the account row in sync with both the backend enrollment flag and
      // the remaining WebAuthn registrations after the modal closes.
      try {
        await stytch.user.get();
      } catch (err) {
        // Unenrollment has already committed; preserve that success even if
        // the local cache needs a later navigation to refresh.
        console.error(
          "Failed to refresh Stytch user after passkey removal",
          err,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["2fa-status"] });
    },
  });
}
